import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@csa/web-foundation";
import type { Page } from "@csa/web-foundation";
import type {
  CustomerSearchBy,
  FindPartiesResult,
  PartyDetail,
  PartySummary,
  ResetPasswordResult,
  UpdatePartyInput,
} from "../../api/party-contracts";

// Customer (müşteri) reads/writes against /v1/parties. No business logic in the
// browser: the API is the boundary and re-enforces party:read / party:write /
// customer.account:reset (+ step-up) on every call. The step-up re-auth for the
// password reset is handled transparently by the shared ApiClient (403/CSA-1007 →
// interactive MFA → retry once), so these hooks just surface the eventual result.

const PARTIES = ["parties"];
const detailKey = (id: string) => [...PARTIES, "detail", id];

/** GET /v1/parties — the default masked-free customer list. party:read.
 *  `enabled` lets the surface skip the doomed list read while a search is active.
 *
 *  GAP-AUDIT-3 D12 — this was a plain useQuery over a Page envelope, so
 *  `nextCursor` was silently discarded and EVERY customer past the first page was
 *  unreachable from this surface. Name search is own-book scoped, so it is not a
 *  substitute for the list. Now a keyset useInfiniteQuery, mirroring the DSAR
 *  register's "daha fazla" pattern. */
export function useCustomers(enabled = true) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: [...PARTIES, "list"],
    enabled,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      api.get<Page<PartySummary>>(
        pageParam ? `/v1/parties?cursor=${encodeURIComponent(pageParam)}` : "/v1/parties",
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

/** GET /v1/parties/find?{phone|name}= — search by normalized phone or own-book name.
 *  party:read. Enabled only for a non-empty term (an empty search falls back to the
 *  default list). */
export function useCustomerSearch(by: CustomerSearchBy, term: string) {
  const api = useApi();
  const q = term.trim();
  return useQuery({
    queryKey: [...PARTIES, "find", by, q],
    enabled: q.length > 0,
    queryFn: () => api.get<FindPartiesResult>(`/v1/parties/find?${by}=${encodeURIComponent(q)}`),
  });
}

/** GET /v1/parties/:id — party detail incl. hasAccount + masked TCKN. party:read. */
export function useCustomerDetail(id: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: id ? detailKey(id) : [...PARTIES, "detail", "none"],
    enabled: id !== null,
    queryFn: () => api.get<PartyDetail>(`/v1/parties/${id}`),
  });
}

/** PATCH /v1/parties/:id — edit name + email (phone/TCKN rejected server-side).
 *  party:write, audited. On success, refetch the detail + the list. */
export function useUpdateCustomer(id: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePartyInput) => api.patch<PartySummary>(`/v1/parties/${id}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: detailKey(id) });
      void qc.invalidateQueries({ queryKey: [...PARTIES, "list"] });
    },
  });
}

/** POST /v1/parties/:id/reset-password — SMTP-free admin reset returning a one-time
 *  temporary password. customer.account:reset + STEP-UP (handled by the ApiClient).
 *  The temp value is NEVER cached/invalidated — it lives only in the reveal dialog. */
export function useResetPassword(id: string) {
  const api = useApi();
  return useMutation({
    mutationFn: () => api.post<ResetPasswordResult>(`/v1/parties/${id}/reset-password`),
  });
}
